/**
 * Peças gráficas do redesign de Clientes: anel de saúde, sparkline e número
 * que conta até o valor na primeira carga. Cada uma traz rótulo textual.
 */
import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

import { CURVA_ENTRADA, DURACAO, prefereMenosMovimento } from "./movimento";

const RAIO_DO_ANEL = 18;
const CIRCUNFERENCIA_DO_ANEL = 2 * Math.PI * RAIO_DO_ANEL;

/** Anel de saúde (0–100) na cor da faixa; o traço cresce a partir de zero. */
export function AnelDeSaude({ saude, faixa, atraso = 0, rotulo, layoutId }) {
  const deslocamento = CIRCUNFERENCIA_DO_ANEL * (1 - Math.max(0, Math.min(100, saude)) / 100);
  return (
    <motion.span
      className={`cl-ring t-${faixa}`}
      role="img"
      aria-label={rotulo || `Saúde ${saude}%`}
      layoutId={layoutId}
      transition={{ duration: DURACAO.painel, ease: CURVA_ENTRADA }}
    >
      <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
        <circle className="bg" cx="22" cy="22" r={RAIO_DO_ANEL} />
        <motion.circle
          className="fg"
          cx="22"
          cy="22"
          r={RAIO_DO_ANEL}
          strokeDasharray={CIRCUNFERENCIA_DO_ANEL}
          initial={{ strokeDashoffset: CIRCUNFERENCIA_DO_ANEL }}
          animate={{ strokeDashoffset: deslocamento }}
          transition={{ duration: DURACAO.barras, ease: CURVA_ENTRADA, delay: atraso }}
        />
      </svg>
      <b aria-hidden="true">{saude}</b>
    </motion.span>
  );
}

const LARGURA_SPARK = 84;
const ALTURA_SPARK = 28;

/** Linha + área de uma série curta; sem série (ou toda zerada), nada. */
export function Sparkline({ serie, rotulo }) {
  if (!serie || serie.length < 2 || serie.every((v) => !v)) return null;
  const max = Math.max(...serie);
  const min = Math.min(...serie);
  const pontos = serie.map((v, i) => [
    ((i / (serie.length - 1)) * LARGURA_SPARK).toFixed(1),
    (ALTURA_SPARK - 4 - ((v - min) / (max - min || 1)) * (ALTURA_SPARK - 8) + 2).toFixed(1),
  ]);
  const linha = `M${pontos.map((p) => p.join(" ")).join(" L")}`;
  return (
    <svg className="cl-spark" viewBox={`0 0 ${LARGURA_SPARK} ${ALTURA_SPARK}`} role="img" aria-label={rotulo}>
      <title>{rotulo}</title>
      <motion.path
        className="cl-sp-a"
        d={`${linha} L${LARGURA_SPARK} ${ALTURA_SPARK} L0 ${ALTURA_SPARK} Z`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: DURACAO.barras, delay: 0.2 }}
      />
      <motion.path
        className="cl-sp-l"
        d={linha}
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: DURACAO.barras, ease: CURVA_ENTRADA, delay: 0.15 }}
      />
    </svg>
  );
}

const DURACAO_CONTAGEM_MS = 600;
const contados = new Set();

/**
 * Conta de 0 até `valor` em 600 ms (ease-out) só na primeira vez que a
 * `chave` aparece na sessão; depois mostra o valor direto.
 */
export function NumeroAnimado({ valor, chave, formatar = (n) => String(n) }) {
  const primeiraVez = useRef(!contados.has(chave) && !prefereMenosMovimento());
  const [mostrado, setMostrado] = useState(primeiraVez.current ? 0 : valor);

  useEffect(() => {
    if (!primeiraVez.current) {
      setMostrado(valor);
      return undefined;
    }
    primeiraVez.current = false;
    contados.add(chave);
    let quadro;
    const inicio = performance.now();
    const passo = (agora) => {
      const t = Math.min(1, (agora - inicio) / DURACAO_CONTAGEM_MS);
      const suavizado = 1 - (1 - t) ** 3;
      setMostrado(Math.round(valor * suavizado));
      if (t < 1) quadro = requestAnimationFrame(passo);
    };
    quadro = requestAnimationFrame(passo);
    return () => cancelAnimationFrame(quadro);
  }, [valor, chave]);

  return formatar(mostrado);
}

/** Barra que cresce da esquerda (scaleX), para as barras "quem espera quem" e de progresso. */
export function BarraCrescente({ proporcao, className, style, atraso = 0.1 }) {
  return (
    <motion.i
      className={className}
      style={{ ...style, transformOrigin: "left center" }}
      initial={{ scaleX: 0 }}
      animate={{ scaleX: Math.max(0, Math.min(1, proporcao)) }}
      transition={{ duration: DURACAO.barras, ease: CURVA_ENTRADA, delay: atraso }}
    />
  );
}
